
(function () {
  'use strict';

  const INIT_ATTR = 'data-soh-init';

  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  async function cartFetch(url, data) {
    const resp = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(data),
    });
    if (!resp.ok) throw new Error(resp.statusText);
    return resp.json();
  }

  /* ─────────────────────────────────────────────────────────
     SeenOnHer — per-section controller
  ───────────────────────────────────────────────────────── */
  class SeenOnHer {
    constructor(section) {
      if (section.hasAttribute(INIT_ATTR)) return;
      section.setAttribute(INIT_ATTR, '1');

      this.section = section;
      this.sectionId = section.dataset.sectionId;
      this.config = (window.SOH_CONFIG && window.SOH_CONFIG[this.sectionId]) || {};

      this.track = section.querySelector('.soh-track');
      this.cards = Array.from(section.querySelectorAll('.soh-card'));
      this.btnPrev = section.querySelector('.soh-nav__btn--prev');
      this.btnNext = section.querySelector('.soh-nav__btn--next');
      this.dotsContainer = section.querySelector('.soh-dots');
      this.modal = document.getElementById(`soh-modal-${this.sectionId}`);
      this.modalVideo = document.getElementById(`soh-modal-video-${this.sectionId}`);

      if (!this.track || this.cards.length === 0) return;

      this.currentIndex = 0;
      this.isAnimating = false;
      this.isMobile = window.innerWidth <= 749;
      this.slidesToShow = parseInt(this.config.slidesToShow, 10) || 3;
      this.slidesToShowMobile = parseInt(this.config.slidesToShowMobile, 10) || 2;
      this.cardHeightVw = parseInt(this.config.cardHeightVw, 10) || 75;

      this._init();
    }

    _init() {
      this._applySlidesToShow();
      this._buildDots();
      this._setupVideoObserver();
      this._setupCardInteractions();
      this._setupVariantSelectors();
      this._setupNavButtons();
      this._setupModal();
      this._setupMobileDotSync();
      this._updateNav();
      this._handleResize();
    }

    /* ── Apply slides-to-show via CSS custom property ── */
    _applySlidesToShow() {
      this.section.style.setProperty('--soh-slides', this.slidesToShow);
      this.section.style.setProperty('--soh-slides-mobile', this.slidesToShowMobile);
      this.section.style.setProperty('--soh-card-h', this.cardHeightVw);
    }

    /* ── Dots (mobile) ── */
    _buildDots() {
      if (!this.dotsContainer) return;
      this.dotsContainer.innerHTML = '';
      this.cards.forEach((_, i) => {
        const dot = document.createElement('button');
        dot.className = `soh-dot${i === 0 ? ' soh-dot--active' : ''}`;
        dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
        dot.addEventListener('click', () => this._scrollToCard(i));
        this.dotsContainer.appendChild(dot);
      });
    }

    _updateDots(index) {
      const dots = this.dotsContainer?.querySelectorAll('.soh-dot');
      if (!dots) return;
      dots.forEach((d, i) => d.classList.toggle('soh-dot--active', i === index));
    }

    /* ── IntersectionObserver: lazy load + autoplay ── */
    _setupVideoObserver() {
      const options = { threshold: 0.5 };

      this.videoObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const card = entry.target;
          const video = card.querySelector('.soh-card__video');
          if (!video) return;

          if (entry.isIntersecting) {
            if (!video.src && video.dataset.src) {
              video.src = video.dataset.src;
            }
            if (video.src && video.paused) {
              const playPromise = video.play();
              if (playPromise !== undefined) {
                playPromise
                  .then(() => {
                    card.classList.add('soh-playing');
                    this._togglePlayIcons(card, true);
                    const thumb = card.querySelector('.soh-card__thumb');
                    if (thumb) thumb.classList.add('soh-thumb--hidden');
                  })
                  .catch(() => {
                    card.classList.remove('soh-playing');
                    this._togglePlayIcons(card, false);
                  });
              }
            }
          } else {
            if (!video.paused) {
              video.pause();
              card.classList.remove('soh-playing');
              this._togglePlayIcons(card, false);
            }
          }
        });
      }, options);

      this.cards.forEach((card) => this.videoObserver.observe(card));
    }

    _togglePlayIcons(card, isPlaying) {
      const iconPlay = card.querySelector('.soh-icon-play');
      const iconPause = card.querySelector('.soh-icon-pause');
      if (iconPlay) iconPlay.style.display = isPlaying ? 'none' : '';
      if (iconPause) iconPause.style.display = isPlaying ? '' : 'none';
    }

    /* ── Card Interactions: click-to-play/modal, mute, ATC ── */
    _setupCardInteractions() {
      this.cards.forEach((card) => {
        const media = card.querySelector('.soh-card__media');
        const video = card.querySelector('.soh-card__video');
        const muteBtn = card.querySelector('.soh-card__mute-btn');
        const atcBtn = card.querySelector('.soh-card__atc');

        if (media) {
          media.addEventListener('click', (e) => {
            if (e.target.closest('.soh-card__mute-btn')) return;
            if (e.target.closest('.soh-card__variants')) return;
            if (e.target.closest('.soh-card__image-link')) return;

            if (window.innerWidth <= 749) {
              this._openModal(card);
            } else {
              if (!video) return;
              if (video.paused) {
                if (!video.src && video.dataset.src) video.src = video.dataset.src;
                video.play().then(() => {
                  card.classList.add('soh-playing');
                  this._togglePlayIcons(card, true);
                });
              } else {
                video.pause();
                card.classList.remove('soh-playing');
                this._togglePlayIcons(card, false);
              }
            }
          });

          media.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              media.click();
            }
          });
        }

        if (muteBtn && video) {
          muteBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            const muted = !video.muted;
            video.muted = muted;
            muteBtn.dataset.muted = muted ? 'true' : 'false';
            const iconMuted = muteBtn.querySelector('.soh-icon-muted');
            const iconUnmuted = muteBtn.querySelector('.soh-icon-unmuted');
            if (iconMuted) iconMuted.style.display = muted ? '' : 'none';
            if (iconUnmuted) iconUnmuted.style.display = muted ? 'none' : '';
          });
        }

        if (atcBtn) {
          atcBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            /* Use currently selected variant from card's data */
            const variantId = card.dataset.variantId;
            if (!variantId) return;
            this._addToCart(atcBtn, variantId);
          });
        }
      });
    }

    /* ── Variant Selectors (card hover overlay) ── */
    _setupVariantSelectors() {
      this.cards.forEach((card) => {
        const variantBtns = card.querySelectorAll('.soh-variant-btn');
        if (!variantBtns.length) return;

        /* Ensure first available variant is selected on init */
        const firstSelected = card.querySelector('.soh-variant-btn--selected:not([disabled])');
        if (!firstSelected) {
          const firstAvail = card.querySelector('.soh-variant-btn:not([disabled])');
          if (firstAvail) {
            firstAvail.classList.add('soh-variant-btn--selected');
            card.dataset.variantId = firstAvail.dataset.variantId;
            const atcBtn = card.querySelector('.soh-card__atc');
            if (atcBtn) atcBtn.dataset.variantId = firstAvail.dataset.variantId;
          }
        } else {
          /* Make sure card and ATC reflect the selected btn */
          card.dataset.variantId = firstSelected.dataset.variantId;
          const atcBtn = card.querySelector('.soh-card__atc');
          if (atcBtn) atcBtn.dataset.variantId = firstSelected.dataset.variantId;
        }

        variantBtns.forEach((btn) => {
          btn.addEventListener('click', (e) => {
            e.stopPropagation();
            if (btn.disabled) return;

            const variantId = btn.dataset.variantId;
            const variantPrice = btn.dataset.variantPrice;

            card.dataset.variantId = variantId;

            const priceEl = card.querySelector('.soh-card__price');
            if (priceEl && variantPrice) priceEl.textContent = variantPrice;

            const atcBtn = card.querySelector('.soh-card__atc');
            if (atcBtn) atcBtn.dataset.variantId = variantId;

            variantBtns.forEach((b) => b.classList.remove('soh-variant-btn--selected'));
            btn.classList.add('soh-variant-btn--selected');
          });
        });
      });
    }

    /* ── Desktop Nav Buttons ── */
    _setupNavButtons() {
      if (this.btnPrev) {
        this.btnPrev.addEventListener('click', () => {
          if (this.currentIndex > 0) this._navigateTo(this.currentIndex - 1);
        });
      }
      if (this.btnNext) {
        this.btnNext.addEventListener('click', () => {
          const slides = this.isMobile ? this.slidesToShowMobile : this.slidesToShow;
          const maxIndex = Math.max(0, this.cards.length - slides);
          if (this.currentIndex < maxIndex) this._navigateTo(this.currentIndex + 1);
        });
      }
    }

    _navigateTo(index) {
      if (this.isMobile) return;
      this.currentIndex = index;
      const cardWidth = this.cards[0].offsetWidth + 24;
      this.track.style.transform = `translateX(-${index * cardWidth}px)`;
      this._updateNav();
      this._updateDots(index);
    }

    _scrollToCard(index) {
      if (!this.isMobile) {
        this._navigateTo(index);
        return;
      }
      const wrapper = this.section.querySelector('.soh-track-wrapper');
      const card = this.cards[index];
      if (!wrapper || !card) return;
      wrapper.scrollTo({ left: card.offsetLeft - 20, behavior: 'smooth' });
    }

    _updateNav() {
      if (!this.btnPrev || !this.btnNext) return;
      const slides = this.isMobile ? this.slidesToShowMobile : this.slidesToShow;
      const maxIndex = Math.max(0, this.cards.length - slides);
      this.btnPrev.disabled = this.currentIndex <= 0;
      this.btnNext.disabled = this.currentIndex >= maxIndex;
    }

    _setupMobileDotSync() {
      const wrapper = this.section.querySelector('.soh-track-wrapper');
      if (!wrapper) return;

      const onScroll = debounce(() => {
        if (!this.isMobile) return;
        let closestIndex = 0;
        let closestDist = Infinity;
        this.cards.forEach((card, i) => {
          const dist = Math.abs(card.getBoundingClientRect().left - wrapper.getBoundingClientRect().left);
          if (dist < closestDist) {
            closestDist = dist;
            closestIndex = i;
          }
        });
        this._updateDots(closestIndex);
      }, 80);

      wrapper.addEventListener('scroll', onScroll, { passive: true });
    }

    _handleResize() {
      const ro = new ResizeObserver(
        debounce(() => {
          const wasMobile = this.isMobile;
          this.isMobile = window.innerWidth <= 749;
          if (wasMobile !== this.isMobile && !this.isMobile) {
            this.currentIndex = 0;
            this.track.style.transform = 'translateX(0)';
            this._updateNav();
          }
        }, 200)
      );
      ro.observe(document.documentElement);
    }

    /* ─────────────────────────────────────────────────────────
       AJAX Cart — Dawn-native integration
    ───────────────────────────────────────────────────────── */
    async _addToCart(btn, variantId, qty = 1) {
      btn.classList.add('soh-loading');
      const originalText = btn.textContent;
      btn.textContent = '';

      try {
        await cartFetch('/cart/add.js', {
          items: [{ id: parseInt(variantId, 10), quantity: qty }],
        });

        this._triggerDawnCartOpen();
        await this._refreshCartCount();

        btn.textContent = '✓ Added';
        setTimeout(() => {
          btn.textContent = originalText;
          btn.classList.remove('soh-loading');
        }, 1800);
      } catch (err) {
        console.error('[SeenOnHer] Cart error:', err);
        btn.textContent = 'Try again';
        btn.classList.remove('soh-loading');
        setTimeout(() => (btn.textContent = originalText), 2000);
      }
    }

    _triggerDawnCartOpen() {
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        if (typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        }
        if (typeof cartDrawer.renderContents === 'function') {
          fetch(`${window.Shopify?.routes?.root || '/'}?section_id=cart-drawer`)
            .then((r) => r.text())
            .then((html) => {
              cartDrawer.renderContents({ sections: { 'cart-drawer': html } });
              cartDrawer.open();
            })
            .catch(() => {});
        }
      }

      const cartNotification = document.querySelector('cart-notification');
      if (cartNotification && typeof cartNotification.open === 'function') {
        cartNotification.open();
      }

      document.dispatchEvent(
        new CustomEvent('cart:add', { bubbles: true, detail: { source: 'seen-on-her' } })
      );

      document.documentElement.dispatchEvent(
        new CustomEvent('cart-drawer:open', { bubbles: true })
      );

      this._refreshCartSection();
    }

    async _refreshCartSection() {
      try {
        const sections = ['cart-drawer', 'cart-icon-bubble'];
        const url = `${window.Shopify?.routes?.root || '/'}?sections=${sections.join(',')}`;
        const resp = await fetch(url);
        const data = await resp.json();

        if (data['cart-drawer']) {
          const drawer = document.getElementById('CartDrawer');
          if (drawer) {
            const temp = document.createElement('div');
            temp.innerHTML = data['cart-drawer'];
            const newDrawer = temp.querySelector('#CartDrawer');
            if (newDrawer) drawer.innerHTML = newDrawer.innerHTML;
          }
        }

        if (data['cart-icon-bubble']) {
          const bubble = document.getElementById('cart-icon-bubble');
          if (bubble) {
            const temp = document.createElement('div');
            temp.innerHTML = data['cart-icon-bubble'];
            const newBubble = temp.querySelector('#cart-icon-bubble');
            if (newBubble) bubble.innerHTML = newBubble.innerHTML;
          }
        }
      } catch (e) {}
    }

    async _refreshCartCount() {
      try {
        const resp = await fetch('/cart.js');
        const cart = await resp.json();
        const countEls = document.querySelectorAll(
          '.cart-count-bubble [aria-hidden], #CartIconBubble .visually-hidden, .cart__count'
        );
        countEls.forEach((el) => {
          if (el.tagName === 'SPAN' && el.hasAttribute('aria-hidden')) {
            el.textContent = cart.item_count;
          }
        });
      } catch (e) {}
    }

    /* ─────────────────────────────────────────────────────────
       Video Modal
    ───────────────────────────────────────────────────────── */
    _setupModal() {
      if (!this.modal) return;

      const backdrop = this.modal.querySelector('.soh-modal__backdrop');
      const closeBtn = this.modal.querySelector('.soh-modal__close');
      const modalAtc = this.modal.querySelector('.soh-modal__atc');

      if (backdrop) backdrop.addEventListener('click', () => this._closeModal());
      if (closeBtn) closeBtn.addEventListener('click', () => this._closeModal());

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !this.modal.hidden) this._closeModal();
      });

      if (modalAtc) {
        modalAtc.addEventListener('click', () => {
          const variantId = modalAtc.dataset.variantId;
          if (!variantId) return;
          this._addToCart(modalAtc, variantId);
        });
      }
    }

    _openModal(card) {
      if (!this.modal || !this.modalVideo) return;

      const video = card.querySelector('.soh-card__video');
      const title = card.dataset.productTitle;
      const price = card.dataset.productPrice;
      const variantId = card.dataset.variantId;
      const productUrl = card.dataset.productUrl || '#';
      const src = video?.src || video?.dataset?.src;

      const modalTitle = this.modal.querySelector('.soh-modal__title');
      const modalPrice = this.modal.querySelector('.soh-modal__price');
      const modalAtc = this.modal.querySelector('.soh-modal__atc');
      const modalTitleLink = this.modal.querySelector('.soh-modal__title-link');
      const modalPriceLink = this.modal.querySelector('.soh-modal__price-link');
      const modalVariants = this.modal.querySelector('.soh-modal__variants');

      if (modalTitle) modalTitle.textContent = title || '';
      if (modalPrice) modalPrice.textContent = price || '';
      if (modalTitleLink) modalTitleLink.href = productUrl;
      if (modalPriceLink) modalPriceLink.href = productUrl;

      if (modalAtc) {
        modalAtc.dataset.variantId = variantId || '';
        modalAtc.textContent = card.dataset.atcLabel || 'Add To Cart';
      }

      /* Build variant buttons in modal */
      if (modalVariants) {
        modalVariants.innerHTML = '';
        const variantBtns = card.querySelectorAll('.soh-variant-btn');
        if (variantBtns.length) {
          variantBtns.forEach((origBtn) => {
            const btn = origBtn.cloneNode(true);
            /* Mirror selected state from card */
            if (origBtn.classList.contains('soh-variant-btn--selected')) {
              btn.classList.add('soh-variant-btn--selected');
            }
            btn.addEventListener('click', () => {
              if (btn.disabled) return;
              const vid = btn.dataset.variantId;
              const vPrice = btn.dataset.variantPrice;

              /* Update modal ATC */
              if (modalAtc) modalAtc.dataset.variantId = vid;
              if (modalPrice && vPrice) modalPrice.textContent = vPrice;

              /* Sync back to card */
              card.dataset.variantId = vid;
              const cardPriceEl = card.querySelector('.soh-card__price');
              if (cardPriceEl && vPrice) cardPriceEl.textContent = vPrice;
              card.querySelectorAll('.soh-variant-btn').forEach((b) => b.classList.remove('soh-variant-btn--selected'));
              const cardOrigBtn = card.querySelector(`.soh-variant-btn[data-variant-id="${vid}"]`);
              if (cardOrigBtn) cardOrigBtn.classList.add('soh-variant-btn--selected');

              /* Highlight in modal */
              modalVariants.querySelectorAll('.soh-variant-btn').forEach((b) => b.classList.remove('soh-variant-btn--selected'));
              btn.classList.add('soh-variant-btn--selected');
            });
            modalVariants.appendChild(btn);
          });
        }
      }

      if (src) {
        this.modalVideo.src = src;
        this.modalVideo.muted = false;
        this.modalVideo.play().catch(() => {});
      }

      this.modal.hidden = false;
      document.body.style.overflow = 'hidden';

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.modal.classList.add('soh-modal--visible');
        });
      });

      this.modal.focus();

      if (video && !video.paused) video.pause();
    }

    _closeModal() {
      if (!this.modal) return;

      this.modal.classList.remove('soh-modal--visible');
      document.body.style.overflow = '';

      setTimeout(() => {
        this.modal.hidden = true;
        if (this.modalVideo) {
          this.modalVideo.pause();
          this.modalVideo.src = '';
        }
      }, 320);
    }
  }

  /* Init all section instances */
  function initAll() {
    document
      .querySelectorAll('[id^="seen-on-her-"]')
      .forEach((el) => new SeenOnHer(el));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector('[id^="seen-on-her-"]');
    if (section) new SeenOnHer(section);
  });
})();





/* SIZE CHART PAGE JS Start */



(function () {
  'use strict';

  const INIT_ATTR = 'data-sc-init';

  class SizeChart {
    constructor(section) {
      if (section.hasAttribute(INIT_ATTR)) return;
      section.setAttribute(INIT_ATTR, '1');

      this.section    = section;
      this.activeUnit = 'cm'; // default

      // Unit toggle buttons
      this.unitBtns   = section.querySelectorAll('.sc-unit-btn');

      // Country tab buttons & panels
      this.tabs        = section.querySelectorAll('.sc-tab');
      this.panels      = section.querySelectorAll('.sc-panel');

      // All cm / inch value cells and header unit labels
      this.cmVals      = section.querySelectorAll('.sc-val--cm');
      this.inchVals    = section.querySelectorAll('.sc-val--inch');
      this.cmLabels    = section.querySelectorAll('.sc-unit-label--cm');
      this.inchLabels  = section.querySelectorAll('.sc-unit-label--inch');

      this._bindUnitToggle();
      this._bindTabs();
    }

    /* ── Unit Toggle ── */
    _bindUnitToggle() {
      this.unitBtns.forEach((btn) => {
        btn.addEventListener('click', () => {
          const unit = btn.dataset.unit;
          if (unit === this.activeUnit) return;
          this.activeUnit = unit;
          this._applyUnit(unit);
        });
      });
    }

    _applyUnit(unit) {
      const isCm = unit === 'cm';

      /* Toggle active state on buttons */
      this.unitBtns.forEach((btn) => {
        const active = btn.dataset.unit === unit;
        btn.classList.toggle('sc-unit-btn--active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      /* Show/hide CM values */
      this.cmVals.forEach((el) => el.classList.toggle('sc-hidden', !isCm));
      this.inchVals.forEach((el) => el.classList.toggle('sc-hidden', isCm));

      /* Show/hide header unit labels */
      this.cmLabels.forEach((el) => el.classList.toggle('sc-hidden', !isCm));
      this.inchLabels.forEach((el) => el.classList.toggle('sc-hidden', isCm));
    }

    /* ── Country Tabs ── */
    _bindTabs() {
      if (this.tabs.length === 0) return;

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          const targetId = tab.getAttribute('aria-controls');
          this._activateTab(tab, targetId);
        });

        /* Keyboard: arrow keys for accessible tab navigation */
        tab.addEventListener('keydown', (e) => {
          const tabArr = Array.from(this.tabs);
          const idx    = tabArr.indexOf(tab);
          let next     = -1;

          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
            next = (idx + 1) % tabArr.length;
          } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
            next = (idx - 1 + tabArr.length) % tabArr.length;
          } else if (e.key === 'Home') {
            next = 0;
          } else if (e.key === 'End') {
            next = tabArr.length - 1;
          }

          if (next >= 0) {
            e.preventDefault();
            tabArr[next].focus();
            tabArr[next].click();
          }
        });
      });
    }

    _activateTab(activeTab, panelId) {
      /* Deactivate all tabs */
      this.tabs.forEach((tab) => {
        tab.classList.remove('sc-tab--active');
        tab.setAttribute('aria-selected', 'false');
      });

      /* Hide all panels */
      this.panels.forEach((panel) => {
        panel.classList.remove('sc-panel--active');
      });

      /* Activate clicked tab */
      activeTab.classList.add('sc-tab--active');
      activeTab.setAttribute('aria-selected', 'true');

      /* Show target panel */
      const targetPanel = document.getElementById(panelId);
      if (targetPanel) {
        targetPanel.classList.add('sc-panel--active');

        /* Re-apply current unit to newly visible panel */
        this._applyUnit(this.activeUnit);
      }
    }
  }

  /* ── Init all section instances ── */
  function initAll() {
    document.querySelectorAll('.sc-section').forEach((el) => new SizeChart(el));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* ── Shopify Theme Editor: re-init on section reload ── */
  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector('.sc-section');
    if (section) {
      section.removeAttribute(INIT_ATTR);
      new SizeChart(section);
    }
  });

  /* ── Theme Editor: re-init on block change (column toggle etc.) ── */
  document.addEventListener('shopify:block:select', (e) => {
    const section = e.target.closest('.sc-section');
    if (section) {
      section.removeAttribute(INIT_ATTR);
      new SizeChart(section);
    }
  });

})();





/**
 * ============================================================
 * KORVÉY — Help Center | help-center.js
 * Dawn Theme Compatible
 * – FAQ accordion with smooth height animation
 * – Keyboard accessible (Enter, Space, Escape, Arrow keys)
 * – Theme Editor section:load / block:select support
 * ============================================================
 */

(function () {
  'use strict';

  const INIT_ATTR = 'data-hc-init';

  class HelpCenter {
    constructor(section) {
      if (section.hasAttribute(INIT_ATTR)) return;
      section.setAttribute(INIT_ATTR, '1');

      this.section  = section;
      this.items    = Array.from(section.querySelectorAll('.hc-faq-item'));

      if (this.items.length === 0) return;
      this._bind();
    }

    _bind() {
      this.items.forEach((item) => {
        const btn    = item.querySelector('.hc-faq-question');
        const answer = item.querySelector('.hc-faq-answer');
        if (!btn || !answer) return;

        btn.addEventListener('click', () => this._toggle(item, btn, answer));

        btn.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            this._close(item, btn, answer);
            btn.blur();
          }
        });
      });
    }

    _toggle(item, btn, answer) {
      const isOpen = item.classList.contains('hc-faq-item--open');
      isOpen ? this._close(item, btn, answer) : this._open(item, btn, answer);
    }

    _open(item, btn, answer) {
      /* Close any other open item */
      this.items.forEach((otherItem) => {
        if (otherItem !== item && otherItem.classList.contains('hc-faq-item--open')) {
          const otherBtn    = otherItem.querySelector('.hc-faq-question');
          const otherAnswer = otherItem.querySelector('.hc-faq-answer');
          this._close(otherItem, otherBtn, otherAnswer);
        }
      });

      item.classList.add('hc-faq-item--open');
      btn.setAttribute('aria-expanded', 'true');

      /* Animate open: remove hidden, then set max-height to scrollHeight */
      answer.removeAttribute('hidden');
      answer.style.maxHeight = '0';
      /* Force reflow so transition plays */
      answer.getBoundingClientRect();
      answer.style.maxHeight = answer.scrollHeight + 'px';
    }

    _close(item, btn, answer) {
      item.classList.remove('hc-faq-item--open');
      btn.setAttribute('aria-expanded', 'false');

      answer.style.maxHeight = answer.scrollHeight + 'px';
      answer.getBoundingClientRect();
      answer.style.maxHeight = '0';

      /* Re-add hidden after transition ends */
      answer.addEventListener('transitionend', function handler() {
        answer.setAttribute('hidden', '');
        answer.style.maxHeight = '';
        answer.removeEventListener('transitionend', handler);
      });
    }
  }

  /* ── Init all sections on page ── */
  function initAll() {
    document.querySelectorAll('.hc-section').forEach((el) => new HelpCenter(el));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* ── Theme Editor support ── */
  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector('.hc-section');
    if (section) {
      section.removeAttribute(INIT_ATTR);
      new HelpCenter(section);
    }
  });

  document.addEventListener('shopify:block:select', (e) => {
    const section = e.target.closest('.hc-section');
    if (section) {
      section.removeAttribute(INIT_ATTR);
      new HelpCenter(section);
    }
  });

})();















/* SIZE CHART PAGE JS END */

/**
 * KORVÉY — Explore Our Fabrics
 * Vanilla JS only. No external libraries.
 */
(function () {
  'use strict';

  function debounce(fn, wait) {
    var t;
    return function () {
      clearTimeout(t);
      var args = arguments;
      t = setTimeout(function () { fn.apply(null, args); }, wait);
    };
  }

  function initSwatchNav(root) {
    var swatches = root.querySelectorAll('.kf-swatch');
    var fabrics = root.querySelectorAll('[data-kf-fabric]');
    if (!swatches.length || !fabrics.length) return;

    swatches.forEach(function (swatch) {
      swatch.addEventListener('click', function () {
        var targetId = swatch.getAttribute('data-kf-target');
        var targetEl = root.querySelector('#' + targetId);
        if (!targetEl) return;

        swatches.forEach(function (s) {
          s.classList.remove('is-active');
          s.setAttribute('aria-selected', 'false');
        });
        swatch.classList.add('is-active');
        swatch.setAttribute('aria-selected', 'true');

        fabrics.forEach(function (f) { f.classList.remove('is-active'); });
        targetEl.classList.add('is-active');

        var headerOffset = 100;
        var top = targetEl.getBoundingClientRect().top + window.pageYOffset - headerOffset;
        window.scrollTo({ top: top, behavior: 'smooth' });
      });
    });

    // Update active swatch on scroll
    var onScroll = debounce(function () {
      var viewportMid = window.innerHeight / 2;
      var closest = null;
      var closestDist = Infinity;

      fabrics.forEach(function (f) {
        var rect = f.getBoundingClientRect();
        var dist = Math.abs(rect.top + rect.height / 2 - viewportMid);
        if (dist < closestDist) {
          closestDist = dist;
          closest = f;
        }
      });

      if (!closest) return;
      var id = closest.id;

      swatches.forEach(function (s) {
        var isMatch = s.getAttribute('data-kf-target') === id;
        s.classList.toggle('is-active', isMatch);
        s.setAttribute('aria-selected', isMatch ? 'true' : 'false');
      });
    }, 120);

    window.addEventListener('scroll', onScroll, { passive: true });
  }

  function initScrollAnimations(root) {
    var animatedEls = root.querySelectorAll('[data-kf-animate], [data-kf-fabric]');
    var progressGroups = root.querySelectorAll('[data-kf-progress]');

    if (!('IntersectionObserver' in window)) {
      animatedEls.forEach(function (el) { el.classList.add('kf-in-view'); });
      progressGroups.forEach(function (group) {
        group.querySelectorAll('.kf-progress-fill').forEach(function (fill) {
          fill.classList.add('kf-fill-active');
        });
      });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('kf-in-view');

          var fills = entry.target.querySelectorAll('.kf-progress-fill');
          fills.forEach(function (fill) {
            fill.classList.add('kf-fill-active');
          });

          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.2, rootMargin: '0px 0px -10% 0px' });

    animatedEls.forEach(function (el) { observer.observe(el); });
  }

  function initCompare(root) {
    var toggles = root.querySelectorAll('[data-kf-compare-toggle]');
    var table = root.querySelector('[data-kf-compare-table]');
    var emptyMsg = root.querySelector('[data-kf-compare-empty]');
    if (!toggles.length || !table) return;

    var MAX_COMPARE = 4;
    var selected = [];

    var fields = ['name', 'feels_like', 'compression', 'finish', 'weight', 'best_for', 'availability', 'support', 'softness'];

    function render() {
      var rows = table.querySelectorAll('[data-kf-compare-row]');

      rows.forEach(function (row) {
        var field = row.getAttribute('data-kf-compare-row');
        var headerCell = row.querySelector('th');
        // remove existing data cells
        row.querySelectorAll('td').forEach(function (td) { td.remove(); });

        selected.forEach(function (item) {
          var cell = document.createElement(field === 'name' ? 'th' : 'td');
          if (field === 'name') {
            cell.setAttribute('scope', 'col');
          }
          cell.textContent = item[field] || '—';
          row.appendChild(cell);
        });
      });

      table.style.display = selected.length ? '' : 'none';
      if (emptyMsg) {
        emptyMsg.classList.toggle('is-visible', selected.length === 0);
      }
    }

    toggles.forEach(function (toggle) {
      toggle.addEventListener('click', function () {
        var id = toggle.getAttribute('data-fabric-id');
        var idx = selected.findIndex(function (s) { return s.id === id; });

        if (idx > -1) {
          selected.splice(idx, 1);
          toggle.classList.remove('is-selected');
        } else {
          if (selected.length >= MAX_COMPARE) {
            return;
          }
          var data = { id: id };
          fields.forEach(function (f) {
            data[f] = toggle.getAttribute('data-' + f) || '';
          });
          selected.push(data);
          toggle.classList.add('is-selected');
        }
        render();
      });
    });

    render();
  }

  var KorveyFabrics = {
    init: function (root) {
      if (!root || root.getAttribute('data-kf-initialized')) return;
      root.setAttribute('data-kf-initialized', 'true');
      initSwatchNav(root);
      initScrollAnimations(root);
      initCompare(root);
    }
  };

  window.KorveyFabrics = KorveyFabrics;

  // Auto-init for sections already present on first load
  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('.korvey-fabrics').forEach(function (root) {
      KorveyFabrics.init(root);
    });
  });

  // Re-init on Shopify section reload (theme editor)
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('.korvey-fabrics');
    if (root) KorveyFabrics.init(root);
  });
})();
